import React, { useEffect, useState } from "react";
import { Loader2, Inbox, Lock, LogOut, KeyRound, X, CheckCircle2 } from "lucide-react";
import { Logo } from "../components/site/Logo";
import { fetchContacts, adminLogin, fetchMe, changePassword, setToken, clearToken, getToken, formatApiErrorDetail } from "../lib/api";

function LoginForm({ onSuccess }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const data = await adminLogin(email, password);
      setToken(data.token);
      onSuccess(data.user);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center emerald-radial px-5" data-testid="admin-login">
      <div className="grain absolute inset-0" />
      <form onSubmit={submit} className="glass relative z-10 w-full max-w-md rounded-[1.75rem] p-8 sm:p-10" data-testid="admin-login-form">
        <div className="flex justify-center"><Logo variant="dark" /></div>
        <div className="mt-8 flex items-center justify-center gap-2 text-gold-light">
          <Lock size={16} /> <span className="text-xs font-bold uppercase tracking-[0.25em]">Admin Access</span>
        </div>
        <h1 className="mt-3 text-center font-serif text-3xl font-light text-off-white">Sign in to continue</h1>

        <label className="mt-8 block">
          <span className="mb-2 block text-xs font-semibold uppercase tracking-wider text-off-white/60">Email</span>
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="admin-email"
            className="w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-off-white placeholder-off-white/30 outline-none transition-colors focus:border-gold" placeholder="you@example.com" />
        </label>
        <label className="mt-5 block">
          <span className="mb-2 block text-xs font-semibold uppercase tracking-wider text-off-white/60">Password</span>
          <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="admin-password"
            className="w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-off-white placeholder-off-white/30 outline-none transition-colors focus:border-gold" placeholder="••••••••" />
        </label>

        {error && <p className="mt-4 rounded-lg bg-red-500/15 px-4 py-2.5 text-sm text-red-300" data-testid="admin-login-error">{error}</p>}

        <button type="submit" disabled={loading} data-testid="admin-login-submit"
          className="mt-7 flex w-full items-center justify-center gap-2 rounded-full bg-gold px-7 py-3.5 text-sm font-semibold text-emerald-darkest transition-transform hover:-translate-y-0.5 disabled:opacity-60">
          {loading ? <Loader2 size={18} className="animate-spin" /> : <Lock size={16} />}
          {loading ? "Signing in…" : "Sign in"}
        </button>
      </form>
    </div>
  );
}

function ChangePasswordModal({ onClose }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (next !== confirm) { setError("New passwords do not match."); return; }
    if (next.length < 8) { setError("New password must be at least 8 characters."); return; }
    setLoading(true);
    try {
      await changePassword(current, next);
      setDone(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Could not change password.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-emerald-darkest/70 p-5 backdrop-blur-sm" data-testid="change-password-modal">
      <div className="relative w-full max-w-md rounded-[1.5rem] bg-white p-8 card-shadow">
        <button onClick={onClose} data-testid="change-password-close" className="absolute right-5 top-5 text-charcoal/40 hover:text-charcoal"><X size={20} /></button>
        {done ? (
          <div className="py-6 text-center">
            <CheckCircle2 size={44} className="mx-auto text-emerald-light" />
            <h3 className="mt-4 font-serif text-2xl text-charcoal">Password updated</h3>
            <p className="mt-2 text-sm text-charcoal/60">Use your new password next time you sign in.</p>
            <button onClick={onClose} data-testid="change-password-done" className="mt-6 rounded-full bg-emerald-base px-6 py-2.5 text-sm font-semibold text-off-white">Close</button>
          </div>
        ) : (
          <form onSubmit={submit} data-testid="change-password-form">
            <div className="flex items-center gap-2 text-emerald-base"><KeyRound size={18} /><h3 className="font-serif text-2xl text-charcoal">Change password</h3></div>
            <p className="mt-2 text-sm text-charcoal/60">Enter your current password, then choose a new one (min 8 characters).</p>

            {[["Current password", current, setCurrent, "cp-current"], ["New password", next, setNext, "cp-new"], ["Confirm new password", confirm, setConfirm, "cp-confirm"]].map(([label, val, set, tid]) => (
              <label key={tid} className="mt-5 block">
                <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-charcoal/50">{label}</span>
                <input type="password" value={val} onChange={(e) => set(e.target.value)} required data-testid={tid}
                  className="w-full rounded-xl border border-black/10 bg-bg-light px-4 py-3 text-charcoal outline-none transition-colors focus:border-gold" placeholder="••••••••" />
              </label>
            ))}

            {error && <p className="mt-4 rounded-lg bg-red-500/10 px-4 py-2.5 text-sm text-red-600" data-testid="change-password-error">{error}</p>}

            <button type="submit" disabled={loading} data-testid="change-password-submit"
              className="mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-gold px-7 py-3.5 text-sm font-semibold text-emerald-darkest transition-transform hover:-translate-y-0.5 disabled:opacity-60">
              {loading ? <Loader2 size={18} className="animate-spin" /> : <KeyRound size={16} />}
              {loading ? "Updating…" : "Update password"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

function Dashboard({ onLogout }) {
  const [items, setItems] = useState(null);
  const [showPw, setShowPw] = useState(false);

  useEffect(() => {
    fetchContacts().then(setItems).catch((e) => {
      if (e.response?.status === 401) onLogout();
      else setItems([]);
    });
  }, [onLogout]);

  return (
    <div className="min-h-screen bg-bg-light" data-testid="admin-page">
      {showPw && <ChangePasswordModal onClose={() => setShowPw(false)} />}
      <header className="border-b border-black/5 bg-white/80 backdrop-blur-xl">
        <div className="mx-auto flex max-w-[1200px] items-center justify-between px-6 py-4">
          <Logo />
          <div className="flex items-center gap-3">
            <span className="hidden text-sm font-semibold text-emerald-base sm:inline">Enquiry Admin</span>
            <button onClick={() => setShowPw(true)} data-testid="open-change-password-btn"
              className="flex items-center gap-1.5 rounded-full border border-black/10 px-4 py-2 text-sm font-medium text-charcoal/70 transition-colors hover:border-gold hover:text-gold">
              <KeyRound size={15} /> Change password
            </button>
            <button onClick={onLogout} data-testid="admin-logout-btn"
              className="flex items-center gap-1.5 rounded-full border border-black/10 px-4 py-2 text-sm font-medium text-charcoal/70 transition-colors hover:border-gold hover:text-gold">
              <LogOut size={15} /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[1200px] px-6 py-12">
        <h1 className="font-serif text-4xl text-charcoal">Consultation Enquiries</h1>
        <p className="mt-2 text-sm text-charcoal/60">All submissions from the website contact form.</p>

        {items === null ? (
          <div className="mt-20 flex justify-center text-emerald-base"><Loader2 className="animate-spin" /></div>
        ) : items.length === 0 ? (
          <div className="mt-20 flex flex-col items-center gap-3 text-charcoal/50">
            <Inbox size={40} /> <p>No enquiries yet.</p>
          </div>
        ) : (
          <div className="mt-10 overflow-x-auto rounded-2xl border border-black/5 bg-white card-shadow">
            <table className="w-full text-left text-sm" data-testid="admin-table">
              <thead className="bg-emerald-darkest text-off-white">
                <tr>
                  {["Name", "Organisation", "Email", "Service", "Preferred Date", "Message", "Received"].map((h) => (
                    <th key={h} className="px-4 py-3 font-semibold">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {items.map((c) => (
                  <tr key={c.id} className="border-t border-black/5 align-top hover:bg-off-white" data-testid={`admin-row-${c.id}`}>
                    <td className="px-4 py-3 font-medium text-charcoal">{c.full_name}</td>
                    <td className="px-4 py-3 text-charcoal/70">{c.organisation || "—"}</td>
                    <td className="px-4 py-3 text-charcoal/70">{c.email}</td>
                    <td className="px-4 py-3 text-charcoal/70">{c.service_required || "—"}</td>
                    <td className="px-4 py-3 text-charcoal/70">{c.preferred_date || "—"}</td>
                    <td className="max-w-xs px-4 py-3 text-charcoal/70">{c.message}</td>
                    <td className="px-4 py-3 text-charcoal/50">{new Date(c.created_at).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  );
}

export default function Admin() {
  const [authed, setAuthed] = useState(null); // null=checking, true, false

  useEffect(() => {
    if (!getToken()) { setAuthed(false); return; }
    fetchMe().then(() => setAuthed(true)).catch(() => { clearToken(); setAuthed(false); });
  }, []);

  const logout = () => { clearToken(); setAuthed(false); };

  if (authed === null)
    return <div className="flex min-h-screen items-center justify-center bg-bg-light"><Loader2 className="animate-spin text-emerald-base" /></div>;
  if (!authed) return <LoginForm onSuccess={() => setAuthed(true)} />;
  return <Dashboard onLogout={logout} />;
}
